"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bot,
  Briefcase,
  Globe,
  Mail,
  MessageCircle,
  Phone,
  Rocket,
  Users,
} from "lucide-react";

import { OnboardingHireBuilderWidget } from "~/components/org-onboarding/onboarding-hire-builder-widget";
import type { OnboardingAgentPublicProfile } from "~/lib/org-onboarding/get-onboarding-agent-public";

const NAVY = "#1a2332";

function SectionHeading({ title }: { title: string }) {
  return (
    <div className="mb-3">
      <p className="text-[10px] font-bold tracking-widest uppercase text-white/60">{title}</p>
      <div className="h-px bg-white/20 mt-1.5" />
    </div>
  );
}

function MainSectionHeading({
  title,
  icon: Icon,
  primaryColor,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  primaryColor: string;
}) {
  return (
    <div className="mb-5 flex items-center gap-3">
      {Icon ? <Icon className="w-4 h-4 shrink-0" style={{ color: primaryColor }} /> : null}
      <div>
        <h2 className="text-sm font-black uppercase tracking-widest" style={{ color: NAVY }}>
          {title}
        </h2>
        <div className="h-0.5 rounded-full mt-1 w-8" style={{ backgroundColor: primaryColor }} />
      </div>
    </div>
  );
}

export default function OnboardingAgentProfileClient({
  data,
}: {
  data: OnboardingAgentPublicProfile;
}) {
  const searchParams = useSearchParams();
  const isProfileEmbed = searchParams.get("embed") === "1";
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const { agent, partnerUrl, stats, recentSessions } = data;
  const primaryColor = agent.accentColor || "#4989F7";
  const P = "var(--cv2-primary)";
  const P_TINT = "color-mix(in srgb, var(--cv2-primary) 10%, transparent)";

  const responsibilities = useMemo(
    () => [
      {
        title: "Website onboarding",
        description: "Guide visitors through AI employee setup from your website.",
      },
      {
        title: "Strategy & pricing",
        description: "Recommend plans and business outcomes based on the visitor's website.",
      },
      {
        title: "Agent introduction",
        description: "Introduce the recommended AI employee and onboarding steps.",
      },
      {
        title: "Workspace handoff",
        description: "Complete onboarding and open the customer's workspace when ready.",
      },
    ],
    [],
  );

  const bio =
    agent.welcomeIntro?.trim() ||
    `${agent.displayName} helps businesses onboard and deploy AI employees through a guided hire-builder experience.`;

  if (!isMounted) return null;

  return (
    <>
      <style>{`:root { --cv2-primary: ${primaryColor}; }`}</style>
      <div className="flex min-h-screen bg-white">
        {!isProfileEmbed ? (
          <aside
            className="hidden md:flex flex-col w-72 shrink-0 fixed top-0 left-0 bottom-0 overflow-y-auto"
            style={{ backgroundColor: NAVY }}
          >
            <div className="flex flex-col gap-5 p-6">
              <div className="flex flex-col items-center text-center pt-2">
                <div className="relative mb-3 inline-flex">
                  <img
                    src={agent.avatarUrl}
                    alt={agent.displayName}
                    className="w-24 h-24 rounded-full object-cover border-2 border-white"
                  />
                  <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400" />
                </div>
                <p className="font-bold text-white text-base leading-tight">{agent.displayName}</p>
                <p className="text-xs mt-0.5" style={{ color: primaryColor }}>
                  {agent.jobTitle}
                </p>
              </div>

              {(agent.phone || agent.email) && (
                <div>
                  <SectionHeading title="Contact" />
                  <div className="space-y-2">
                    {agent.phone ? (
                      <a
                        href={`tel:${agent.phone}`}
                        className="flex items-center gap-2 text-white/70 hover:text-white text-xs"
                      >
                        <Phone className="w-3.5 h-3.5 shrink-0" style={{ color: primaryColor }} />
                        {agent.phone}
                      </a>
                    ) : null}
                    {agent.email ? (
                      <a
                        href={`mailto:${agent.email}`}
                        className="flex items-center gap-2 text-white/70 hover:text-white text-xs"
                      >
                        <Mail className="w-3.5 h-3.5 shrink-0" style={{ color: primaryColor }} />
                        {agent.email}
                      </a>
                    ) : null}
                  </div>
                </div>
              )}

              <div>
                <SectionHeading title="Activity" />
                <div className="space-y-2 text-xs text-white/80">
                  <p>Conversations: {stats.totalSessions}</p>
                  <p>Signed up: {stats.signedUpSessions}</p>
                  <p>Contacts: {stats.totalContacts}</p>
                </div>
              </div>
            </div>
          </aside>
        ) : null}

        <main className={`flex-1 ${!isProfileEmbed ? "md:ml-72" : ""}`}>
          <div className="mx-auto max-w-4xl px-6 py-10 md:px-10 md:py-14">
            <div className="flex items-start gap-5 mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center shrink-0 overflow-hidden border-2"
                style={{ borderColor: P_TINT, background: P_TINT }}
              >
                {agent.avatarUrl ? (
                  <img src={agent.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Bot className="w-8 h-8" style={{ color: primaryColor }} />
                )}
              </div>
              <div>
                <h1 className="text-5xl font-black uppercase tracking-tight leading-none" style={{ color: NAVY }}>
                  {agent.displayName}
                </h1>
                <p className="text-lg font-semibold mt-2" style={{ color: primaryColor }}>
                  {agent.jobTitle}
                </p>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1 mb-8 flex-nowrap">
              <Link
                href={partnerUrl}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                <Rocket className="w-4 h-4" /> Start onboarding
              </Link>
              <button
                type="button"
                onClick={() => {
                  const btn = document.querySelector<HTMLButtonElement>(
                    "[data-onboarding-widget-launcher]",
                  );
                  btn?.click();
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]"
                style={{ borderColor: NAVY, color: NAVY }}
              >
                <MessageCircle className="w-4 h-4" /> Chat
              </button>
            </div>

            <div className="mb-10 pl-4 border-l-4" style={{ borderColor: primaryColor }}>
              <p className="font-extrabold text-2xl md:text-3xl leading-tight" style={{ color: NAVY }}>
                {agent.organizationName}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Hire-builder:{" "}
                <Link href={partnerUrl} className="hover:underline" style={{ color: primaryColor }}>
                  {partnerUrl}
                </Link>
              </p>
            </div>

            <section className="mb-12">
              <MainSectionHeading title="Professional Summary" icon={Briefcase} primaryColor={primaryColor} />
              <div className="prose prose-sm max-w-none text-gray-600">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{bio}</ReactMarkdown>
              </div>
            </section>

            <section className="mb-12">
              <MainSectionHeading title="My Responsibilities" icon={Briefcase} primaryColor={primaryColor} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {responsibilities.map((role) => (
                  <div key={role.title} className="rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                    <p className="text-sm font-bold" style={{ color: NAVY }}>
                      {role.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{role.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {recentSessions.length > 0 ? (
              <section className="mb-12">
                <MainSectionHeading title="Recent Onboarding Sessions" icon={Users} primaryColor={primaryColor} />
                <div className="space-y-2">
                  {recentSessions.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-sm"
                    >
                      <div>
                        <p className="font-medium" style={{ color: NAVY }}>
                          {session.visitorName?.trim() ||
                            session.visitorEmail?.trim() ||
                            "Anonymous visitor"}
                        </p>
                        {session.websiteUrl ? (
                          <p className="text-xs text-gray-500 truncate max-w-md">{session.websiteUrl}</p>
                        ) : null}
                      </div>
                      <span className="text-xs uppercase tracking-wide text-gray-400">{session.status}</span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="mb-12">
              <MainSectionHeading title="Deploy" icon={Globe} primaryColor={primaryColor} />
              <p className="text-sm text-gray-600">
                Share the hire-builder link on your website, email campaigns, and partner channels to start
                onboarding conversations with {agent.displayName}.
              </p>
            </section>
          </div>
        </main>
      </div>

      {!isProfileEmbed ? (
        <OnboardingHireBuilderWidget
            partnerUrl={partnerUrl}
            agentName={agent.displayName}
            avatarUrl={agent.avatarUrl}
            accentColor={agent.accentColor}
        />
      ) : null}
    </>
  );
}
